import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar,
  PieChart, Pie, Cell, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const BLUE = "#1F75A8";
const TEAL = "#5DCAA5";
const GOLD = "#B8975A";
const GRID = "rgba(120,120,120,0.10)";
const AXIS = "#9C978D";

const axisProps = { tick: { fill: AXIS, fontSize: 10 }, axisLine: false, tickLine: false };
const tooltipStyle = { fontSize: 12, borderRadius: 8, border: "1px solid #E7E1D6" };

export function VisitorAreaChart({ trend, height = 180 }) {
  const data = trend.labels.map((l, i) => ({ day: l, Visitors: trend.values[i] }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
        <defs>
          <linearGradient id="visFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={BLUE} stopOpacity={0.16} />
            <stop offset="100%" stopColor={BLUE} stopOpacity={0.01} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="day" {...axisProps} />
        <YAxis {...axisProps} width={40} />
        <Tooltip contentStyle={tooltipStyle} />
        <Area type="monotone" dataKey="Visitors" stroke={BLUE} strokeWidth={2} fill="url(#visFill)" dot={{ r: 3, fill: BLUE }} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function VelocityLineChart({ trend, height = 190 }) {
  const data = trend.labels.map((l, i) => ({ day: l, Visitors: trend.visitors[i], Purchases: trend.purchases[i] }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="day" {...axisProps} />
        <YAxis yAxisId="l" {...axisProps} width={40} />
        <YAxis yAxisId="r" orientation="right" {...axisProps} width={30} />
        <Tooltip contentStyle={tooltipStyle} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Line yAxisId="l" type="monotone" dataKey="Visitors" stroke={BLUE} strokeWidth={2} dot={{ r: 3 }} />
        <Line yAxisId="r" type="monotone" dataKey="Purchases" stroke={TEAL} strokeWidth={2} strokeDasharray="4 3" dot={{ r: 3 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function ForecastChart({ chart, height = 210 }) {
  const data = chart.labels.map((m, i) => {
    const low = chart.low[i];
    const high = chart.high[i];
    return {
      month: m,
      Actual: chart.actual[i],
      Projected: chart.projected[i],
      low: low,
      band: low != null && high != null ? high - low : null,
    };
  });
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 6, right: 8, left: -6, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="month" {...axisProps} />
        <YAxis {...axisProps} width={46} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => (v == null ? "—" : `$${Number(v).toLocaleString()}`)} />
        <Area dataKey="low" stackId="band" stroke="none" fill="transparent" connectNulls />
        <Area dataKey="band" stackId="band" stroke="none" fill={BLUE} fillOpacity={0.08} connectNulls />
        <Line type="monotone" dataKey="Projected" stroke={BLUE} strokeWidth={2} strokeDasharray="5 3" dot={{ r: 2 }} connectNulls />
        <Line type="monotone" dataKey="Actual" stroke={BLUE} strokeWidth={2.5} dot={{ r: 4, fill: BLUE }} connectNulls={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function MissionBarChart({ momentum, height = 180 }) {
  const data = momentum.labels.map((l, i) => ({ month: l, Couples: momentum.values[i] }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="month" {...axisProps} />
        <YAxis {...axisProps} width={40} />
        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(31,117,168,0.05)" }} />
        <Bar dataKey="Couples" fill={BLUE} radius={[3, 3, 0, 0]} maxBarSize={44} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function StagePieChart({ stages, height = 200 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie data={stages} dataKey="pct" nameKey="name" innerRadius="52%" outerRadius="80%" paddingAngle={2} stroke="#fff" strokeWidth={2}>
          {stages.map((s, i) => <Cell key={i} fill={s.color} />)}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v}%`, n]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function RevenueTierChart({ tiers, height = 180 }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={tiers} margin={{ top: 6, right: 8, left: -6, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="name" {...axisProps} />
        <YAxis {...axisProps} width={46} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
        <Tooltip contentStyle={tooltipStyle} formatter={(v) => `$${Number(v).toLocaleString()}`} />
        <Bar dataKey="value" radius={[3, 3, 0, 0]} maxBarSize={70}>
          {tiers.map((t, i) => <Cell key={i} fill={t.color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function ContentTypeChart({ byType, height = 190 }) {
  const data = byType.labels.map((l, i) => ({
    name: l, Views: byType.views[i], Saves: byType.saves[i], "Quiz clicks": byType.quiz_clicks[i],
  }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="name" {...axisProps} />
        <YAxis {...axisProps} width={40} />
        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(31,117,168,0.05)" }} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="Views" fill={BLUE} radius={[2, 2, 0, 0]} maxBarSize={22} />
        <Bar dataKey="Saves" fill={TEAL} radius={[2, 2, 0, 0]} maxBarSize={22} />
        <Bar dataKey="Quiz clicks" fill={GOLD} radius={[2, 2, 0, 0]} maxBarSize={22} />
      </BarChart>
    </ResponsiveContainer>
  );
}
